// Propulse Ludo Learning — full-blown classic 4-player Ludo with 4 tokens per side,
// AI-generated learning questions, 7 special-square types, and game-style UI.
//
// Player = Yellow (BL base). Bots = Green (TL), Red (TR), Blue (BR), depending on user-chosen bot count.
// Win: player gets all 4 yellow tokens to the center HOME. If any bot does it first, player loses.
//
// Architecture:
//   ludo/ludoEngine.js   — path coords, special squares, bot AI, capture rules
//   ludo/LudoBoard.jsx   — 15x15 grid visual board + animated tokens (all 4 colors)
//   ludo/LudoDice.jsx    — 3D animated dice + roll button
//   ludo/LudoModals.jsx  — Question / Hazard / Boss / Star / Tip / Win / TopicPicker

import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, RefreshCw, User, Bot, Volume2, VolumeX, MessageSquareText, HelpCircle, Star, Zap, Lightbulb, ArrowBigRight, Shield, Maximize2, Minimize2 } from 'lucide-react';

import {
    squareType, capturesAt, legalMoves, botPickToken, botAnswersCorrectly,
    tokenOuterIndex, activeColorsFor, COLOR_LABELS,
    FALLBACK_QUESTIONS, LEARNING_TIPS, TOPIC_PRESETS,
} from './ludo/ludoEngine';
import LudoBoard from './ludo/LudoBoard';
import LudoDice from './ludo/LudoDice';
import { QuestionModal, StarModal, TipModal, BossModal, WinModal, TopicPicker } from './ludo/LudoModals';
import { ConfettiBurst, Fireworks } from './ludo/LudoConfetti';

const EMPTY_TOKENS = () => ({ yellow: [0, 0, 0, 0], green: [0, 0, 0, 0], red: [0, 0, 0, 0], blue: [0, 0, 0, 0] });

export default function LudoLearning({ game, onPlayed }) {
    // --- setup state
    const [topicPickerOpen, setTopicPickerOpen] = useState(true);
    const [numBots, setNumBots] = useState(3);
    const [activeColors, setActiveColors] = useState(activeColorsFor(3)); // ['yellow', 'green', 'red', 'blue']
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState('');
    const [topic, setTopic] = useState('');
    const [questionBank, setQuestionBank] = useState([]);

    // --- gameplay state
    const [tokens, setTokens] = useState(EMPTY_TOKENS());
    const tokensRef = useRef(tokens);
    useEffect(() => { tokensRef.current = tokens; }, [tokens]);

    const [turn, setTurn] = useState('yellow');
    // Mirror turn in a ref so setTimeout-chained handlers (botRoll → executeMove →
    // handleLanding → endTurn) read the LIVE turn, not the stale render closure.
    // Without this, bot extra-rolls intermittently freeze because `turn` in closure
    // can still be 'yellow' from a prior render, and endTurn(true) silently bails.
    const turnRef = useRef('yellow');
    useEffect(() => { turnRef.current = turn; }, [turn]);
    const doneRef = useRef(null);
    useEffect(() => { doneRef.current = done; });
    const [dice, setDice] = useState(null);
    const [rolling, setRolling] = useState(false);
    // Post-roll floating callout — shows "🎲 6 · ROLL AGAIN!" above the dice that just landed.
    // Cleared after 1.8s. Keyed so consecutive rolls re-trigger the animation.
    const [diceCallout, setDiceCallout] = useState(null);
    const triggerDiceCallout = (color, value) => {
        const isSix = value === 6;
        setDiceCallout({
            color, value, isSix,
            key: Date.now() + Math.random(),
        });
        setTimeout(() => setDiceCallout((cur) => (cur && cur.color === color && cur.value === value ? null : cur)), 1800);
    };
    const [highlighted, setHighlighted] = useState(null);
    const [lastMoved, setLastMoved] = useState(null);

    // --- scoring
    const [score, setScore] = useState(0);
    const [captures, setCaptures] = useState(0);
    const [learnedCount, setLearnedCount] = useState(0);
    const [log, setLog] = useState([]);
    const [done, setDone] = useState(null);
    const [badgeUnlocked, setBadgeUnlocked] = useState(false);

    // --- modals
    const [questionModal, setQuestionModal] = useState(null);
    const [starModal, setStarModal] = useState(null);
    const [tipModal, setTipModal] = useState(null);
    const [bossModal, setBossModal] = useState(null);
    const [winOpen, setWinOpen] = useState(false);

    const [muted, setMuted] = useState(false);
    const pendingTurnEnd = useRef(null);
    // Premium celebratory announcement banner ("ROLL AGAIN!", "CAPTURE!", "HOME!")
    const [announce, setAnnounce] = useState(null);
    const announce_ = (text, tone = 'gold') => {
        const id = Date.now() + Math.random();
        setAnnounce({ text, tone, id });
        setTimeout(() => setAnnounce((a) => (a && a.id === id ? null : a)), 1500);
    };
    // Confetti bursts — each entry has a unique id so the burst remounts and re-animates.
    const [bursts, setBursts] = useState([]);
    const burstIdRef = useRef(0);
    const triggerBurst = (kind = 'capture') => {
        burstIdRef.current += 1;
        const id = burstIdRef.current;
        const cfg = kind === 'home' ? { count: 50, scale: 1.3 } : { count: 30, scale: 1.0 };
        setBursts((b) => [...b, { id, ...cfg }]);
        setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1600);
    };

    // Fullscreen
    const wrapperRef = useRef(null);
    const [isFs, setIsFs] = useState(false);
    useEffect(() => {
        const onChange = () => setIsFs(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        document.addEventListener('webkitfullscreenchange', onChange);
        return () => {
            document.removeEventListener('fullscreenchange', onChange);
            document.removeEventListener('webkitfullscreenchange', onChange);
        };
    }, []);
    const toggleFs = async () => {
        const el = wrapperRef.current;
        if (!el) return;
        try {
            if (!document.fullscreenElement && !document.webkitFullscreenElement) {
                if (el.requestFullscreen) await el.requestFullscreen();
                else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
            } else {
                if (document.exitFullscreen) await document.exitFullscreen();
                else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
            }
        } catch { /* iOS Safari may block; fail silently */ }
    };

    // === Update active colors when bot count changes ===
    useEffect(() => { setActiveColors(activeColorsFor(numBots)); }, [numBots]);

    // === HELPERS ===
    const pushLog = (entry) => setLog((l) => [{ ...entry, t: Date.now() + Math.random() }, ...l].slice(0, 14));

    const playSound = (kind) => {
        if (muted) return;
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            const ctx = new AC();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = kind === 'win' ? 'triangle' : kind === 'capture' ? 'sawtooth' : 'sine';
            const map = { roll: 280, move: 540, correct: 880, wrong: 180, capture: 120, win: 1320, star: 1000, land: 620 };
            o.frequency.value = map[kind] || 440;
            o.connect(g); g.connect(ctx.destination);
            g.gain.setValueAtTime(0.0001, ctx.currentTime);
            g.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
            o.start(); o.stop(ctx.currentTime + 0.36);
        } catch { /* best-effort */ }
    };

    // === AI QUESTION GENERATION (instant start + background hot-swap + 24h cache) ===
    const generateQuestions = async (chosenTopic) => {
        // 1. START IMMEDIATELY with curated bank — zero wait for the user.
        setQuestionBank(FALLBACK_QUESTIONS);
        setTopic(chosenTopic);
        setTopicPickerOpen(false);
        pushLog({ kind: 'system', text: `📚 ${chosenTopic} · curated bank ready — AI brewing in background…` });

        // 2. Try the 24h cache first
        try {
            const { data } = await api.get('/games/ludo_learning/cached-questions', { params: { topic: chosenTopic } });
            if (data?.questions?.length >= 6) {
                setQuestionBank(data.questions);
                pushLog({ kind: 'system', text: `✨ AI-tuned ${data.questions.length} questions live (cached): ${chosenTopic}` });
                return;
            }
        } catch { /* cache miss — fall through to AI gen */ }

        // 3. Background AI generation — silent hot-swap when ready
        setAiLoading(true); setAiError('');
        try {
            const { data } = await api.post('/games/ludo/generate-questions', { topic: chosenTopic, num_questions: 20 });
            const qs = data?.questions || [];
            if (qs.length >= 6) {
                setQuestionBank(qs);
                pushLog({ kind: 'system', text: `✨ AI-tuned ${qs.length} questions now live!` });
            }
        } catch (e) {
            // Silent — game continues on curated bank
            console.warn('Background AI gen failed', e);
            setAiError('');
            pushLog({ kind: 'system', text: '⚠️ AI tutor is slow — sticking with the curated bank.' });
        } finally {
            setAiLoading(false);
        }
    };

    const skipToFallback = () => {
        setQuestionBank(FALLBACK_QUESTIONS);
        setTopic('Curated cables & wires fundamentals');
        setTopicPickerOpen(false);
        pushLog({ kind: 'system', text: `📚 Curated bank · vs ${numBots} bot${numBots > 1 ? 's' : ''}` });
    };

    // === DICE ROLL ===
    const rollDice = () => {
        if (rolling || turn !== 'yellow' || done || questionModal || starModal || tipModal || bossModal || highlighted) return;
        setRolling(true); playSound('roll');
        let n = 0;
        const interval = setInterval(() => {
            setDice(1 + Math.floor(Math.random() * 6));
            n += 1;
            if (n >= 10) {
                clearInterval(interval);
                const final = 1 + Math.floor(Math.random() * 6);
                setDice(final);
                setRolling(false);
                playSound('land');
                triggerDiceCallout('yellow', final);
                if (final === 6) announce_('🎲 SIX! ROLL AGAIN', 'gold');
                onPlayerRolled(final);
            }
        }, 60);
    };

    const botRoll = (color) => {
        if (done) return;
        setRolling(true); playSound('roll');
        let n = 0;
        const interval = setInterval(() => {
            setDice(1 + Math.floor(Math.random() * 6));
            n += 1;
            if (n >= 8) {
                clearInterval(interval);
                const final = 1 + Math.floor(Math.random() * 6);
                setDice(final);
                setRolling(false);
                playSound('land');
                triggerDiceCallout(color, final);
                setTimeout(() => onBotRolled(color, final), 400);
            }
        }, 60);
    };

    // === PLAYER FLOW ===
    const onPlayerRolled = (value) => {
        const legal = legalMoves('yellow', tokensRef.current.yellow, value);
        if (legal.length === 0) {
            pushLog({ kind: 'player', text: `🎲 ${value} — no legal moves` });
            endTurn(value === 6);
            return;
        }
        if (legal.length === 1) {
            executeMove('yellow', legal[0], value);
        } else {
            setHighlighted({ color: 'yellow', indices: legal });
            pushLog({ kind: 'player', text: `🎲 ${value} — pick a token to move` });
        }
    };

    const onPlayerTokenClick = (color, idx) => {
        if (color !== 'yellow' || !highlighted || !dice) return;
        if (!highlighted.indices.includes(idx)) return;
        setHighlighted(null);
        executeMove('yellow', idx, dice);
    };

    // === MOVE EXECUTION ===
    // Hops the token cell-by-cell (Ludo-King style) instead of teleporting.
    // Then resolves capture + handleLanding.
    const HOP_MS = 140;          // delay between successive hops
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const stepToken = (color, idx, pos) => {
        const live = tokensRef.current;
        const updated = { yellow: live.yellow.slice(), green: live.green.slice(), red: live.red.slice(), blue: live.blue.slice() };
        updated[color][idx] = pos;
        tokensRef.current = updated;
        setTokens(updated);
    };

    const executeMove = async (color, idx, value) => {
        playSound('move');
        const liveTokens = tokensRef.current;
        const oldPos = liveTokens[color][idx];
        const targetPos = oldPos === 0 ? 1 : oldPos + value;

        setLastMoved({ color, idx });

        // Releasing from base: pin "pops" out onto the start cell — no hopping.
        if (oldPos === 0) {
            stepToken(color, idx, 1);
            await sleep(220);
        } else {
            // Hop cell by cell so the user can follow the path
            for (let p = oldPos + 1; p <= targetPos; p++) {
                stepToken(color, idx, p);
                playSound('move');
                await sleep(HOP_MS);
            }
        }

        // Resolve captures AFTER the last hop landed
        const liveAfter = tokensRef.current;
        const caps = capturesAt(color, targetPos, liveAfter, activeColors);
        if (caps.length) {
            playSound('capture');
            triggerBurst('capture');
            if (color === 'yellow') announce_('💥 CAPTURE!', 'red');
            else announce_(`💥 ${COLOR_LABELS[color].name.toUpperCase()} CAPTURES YOU!`, 'red');
            const updated = { yellow: liveAfter.yellow.slice(), green: liveAfter.green.slice(), red: liveAfter.red.slice(), blue: liveAfter.blue.slice() };
            let capCount = 0;
            caps.forEach(({ capturedColor, capturedIndices }) => {
                capturedIndices.forEach((i) => { updated[capturedColor][i] = 0; capCount += 1; });
            });
            tokensRef.current = updated;
            setTokens(updated);
            if (color === 'yellow') setCaptures((c) => c + capCount);
            const victims = caps.map((c) => COLOR_LABELS[c.capturedColor].name).join(', ');
            pushLog({ kind: color, text: `${COLOR_LABELS[color].emoji} ${COLOR_LABELS[color].name} captured ${capCount} token${capCount > 1 ? 's' : ''} from ${victims}!` });
        }

        // Reached HOME?
        if (targetPos === 57) {
            playSound('win');
            triggerBurst('home');
            if (color === 'yellow') announce_('🏁 TOKEN HOME!', 'green');
            pushLog({ kind: color, text: `🏁 ${COLOR_LABELS[color].name} token #${idx + 1} reached HOME!` });
            if (color === 'yellow') setScore((s) => s + 30);
            const allHome = tokensRef.current[color].every((p) => p === 57);
            if (allHome) {
                await sleep(400);
                finalize(color === 'yellow');
                return;
            }
        }

        await sleep(220);
        handleLanding(color, targetPos, value, caps.length > 0);
    };

    // === LANDING SQUARE LOGIC ===
    const handleLanding = (color, newPos, value, didCapture) => {
        if (newPos < 1 || newPos > 51) {
            endTurn(value === 6 || didCapture);
            return;
        }
        const outerIdx = tokenOuterIndex(color, newPos);
        const stype = squareType(outerIdx);

        if (color !== 'yellow') {
            handleBotLandedOnSpecial(color, stype, value, didCapture);
            return;
        }

        // Player flow
        if (stype === 'question')      askQuestion('question', value, didCapture);
        else if (stype === 'hazard')   askQuestion('hazard', value, didCapture);
        else if (stype === 'shortcut') askQuestion('shortcut', value, didCapture);
        else if (stype === 'boss')     startBoss(value, didCapture);
        else if (stype === 'star')     showStar(value, didCapture);
        else if (stype === 'tip')      showTip(value, didCapture);
        else                            endTurn(value === 6 || didCapture);
    };

    const handleBotLandedOnSpecial = (color, stype, value, didCapture) => {
        if (stype === 'question' || stype === 'hazard' || stype === 'shortcut') {
            const correct = botAnswersCorrectly();
            if (correct) {
                pushLog({ kind: 'bot', text: `${COLOR_LABELS[color].emoji} ${COLOR_LABELS[color].name} answered ${stype} correctly — rolls again` });
                endTurn(true);
            } else {
                const penalty = stype === 'hazard' ? 6 : stype === 'shortcut' ? 0 : 4;
                if (penalty > 0) stepBack(color, penalty);
                pushLog({ kind: 'bot', text: `${COLOR_LABELS[color].emoji} ${COLOR_LABELS[color].name} got ${stype} wrong — back ${penalty} cells` });
                endTurn(value === 6);
            }
            return;
        }
        if (stype === 'boss') {
            const win = Math.random() < 0.5;
            pushLog({ kind: 'bot', text: win ? `${COLOR_LABELS[color].emoji} ${COLOR_LABELS[color].name} cleared the boss round!` : `${COLOR_LABELS[color].emoji} ${COLOR_LABELS[color].name} failed the boss round` });
            endTurn(value === 6);
            return;
        }
        endTurn(value === 6 || didCapture);
    };

    // === QUESTION FLOWS (player only) ===
    const askQuestion = (kind, value, didCapture) => {
        const q = randomQuestion();
        setQuestionModal({
            kind, question: q,
            onAnswer: (correct) => {
                setQuestionModal(null);
                if (correct === true) {
                    playSound('correct'); setLearnedCount((n) => n + 1);
                    const pts = kind === 'hazard' ? 30 : kind === 'shortcut' ? 25 : 20;
                    setScore((s) => s + pts);
                    pushLog({ kind: 'player', text: `✅ Correct (${kind}) +${pts} pts` });
                    if (kind === 'shortcut') {
                        leapForward('yellow', 10);
                        pushLog({ kind: 'system', text: '🎯 Shortcut unlocked — leap +10 cells!' });
                    }
                    endTurn(true);
                } else if (correct === false) {
                    playSound('wrong');
                    const penalty = kind === 'hazard' ? 6 : kind === 'shortcut' ? 0 : 4;
                    if (penalty > 0) {
                        stepBack('yellow', penalty);
                        pushLog({ kind: 'player', text: `❌ Wrong (${kind}) — back ${penalty} cells` });
                    } else {
                        pushLog({ kind: 'player', text: `❌ Wrong (${kind}) — shortcut locked` });
                    }
                    endTurn(value === 6 || didCapture);
                } else {
                    playSound('wrong');
                    pushLog({ kind: 'player', text: `⏰ Timed out on ${kind}` });
                    endTurn(value === 6 || didCapture);
                }
            },
        });
    };

    const showStar = (value, didCapture) => {
        playSound('star');
        const tip = LEARNING_TIPS[Math.floor(Math.random() * LEARNING_TIPS.length)];
        setStarModal({ ...tip });
        setScore((s) => s + 10); setLearnedCount((n) => n + 1);
        pendingTurnEnd.current = { extra: value === 6 || didCapture };
    };
    const showTip = (value, didCapture) => {
        const tip = LEARNING_TIPS[Math.floor(Math.random() * LEARNING_TIPS.length)];
        setTipModal({ ...tip });
        setScore((s) => s + 5); setLearnedCount((n) => n + 1);
        pendingTurnEnd.current = { extra: value === 6 || didCapture };
    };

    const closeStar = () => { setStarModal(null); endTurn(pendingTurnEnd.current?.extra); pendingTurnEnd.current = null; };
    const closeTip  = () => { setTipModal(null);  endTurn(pendingTurnEnd.current?.extra); pendingTurnEnd.current = null; };

    const startBoss = (value, didCapture) => {
        const qs = sample(questionBank.length ? questionBank : FALLBACK_QUESTIONS, 3);
        setBossModal({ questions: qs });
        pendingTurnEnd.current = { extra: value === 6 || didCapture };
    };

    const completeBoss = (correctCount, total) => {
        setBossModal(null);
        if (correctCount === total) {
            playSound('win'); setScore((s) => s + 100); setLearnedCount((n) => n + total);
            pushLog({ kind: 'player', text: '👑 Boss cleared! +100 pts + leap 4 cells' });
            leapForward('yellow', 4);
            endTurn(true);
        } else if (correctCount >= 2) {
            setScore((s) => s + 50); setLearnedCount((n) => n + correctCount);
            pushLog({ kind: 'player', text: `👑 Boss partial win (${correctCount}/${total}) +50 pts` });
            endTurn(pendingTurnEnd.current?.extra);
        } else {
            stepBack('yellow', 10);
            pushLog({ kind: 'player', text: `💀 Boss failed (${correctCount}/${total}) — back 10 cells` });
            endTurn(pendingTurnEnd.current?.extra);
        }
        pendingTurnEnd.current = null;
    };

    // === MOVE UTILITIES (operate on ref) ===
    const stepBack = (color, n) => {
        const live = tokensRef.current;
        const next = { ...live, [color]: live[color].slice() };
        const idx = lastMoved?.color === color ? lastMoved.idx : next[color].findIndex((p) => p > 0 && p < 52);
        if (idx >= 0 && next[color][idx] > 0) next[color][idx] = Math.max(1, next[color][idx] - n);
        tokensRef.current = next; setTokens(next);
    };
    const leapForward = (color, n) => {
        const live = tokensRef.current;
        const next = { ...live, [color]: live[color].slice() };
        const idx = lastMoved?.color === color ? lastMoved.idx : next[color].findIndex((p) => p > 0 && p < 52);
        if (idx >= 0 && next[color][idx] > 0) next[color][idx] = Math.min(57, next[color][idx] + n);
        tokensRef.current = next; setTokens(next);
    };

    // === TURN MANAGEMENT ===
    // Reads turn from turnRef (always live) and updates BOTH ref and state synchronously
    // to keep all subsequent setTimeout chains in sync. No side effects inside setState callback.
    const endTurn = (extraRoll = false) => {
        if (doneRef.current) return;
        if (extraRoll) {
            const cur = turnRef.current;
            pushLog({ kind: 'system', text: `↻ ${COLOR_LABELS[cur].name} rolls again` });
            if (cur !== 'yellow') setTimeout(() => botRoll(cur), 600);
            return;
        }
        const cur = turnRef.current;
        const idx = activeColors.indexOf(cur);
        const next = activeColors[(idx + 1) % activeColors.length];
        turnRef.current = next;
        setTurn(next);
        if (next !== 'yellow') setTimeout(() => botRoll(next), 700);
    };

    // === BOT ROLL HANDLING ===
    const onBotRolled = (color, value) => {
        const live = tokensRef.current;
        const pickedIdx = botPickToken(color, live[color], live, activeColors, value);
        if (pickedIdx === null) {
            pushLog({ kind: 'bot', text: `${COLOR_LABELS[color].emoji} ${COLOR_LABELS[color].name} rolled ${value} — no legal moves` });
            endTurn(value === 6);
            return;
        }
        pushLog({ kind: 'bot', text: `${COLOR_LABELS[color].emoji} ${COLOR_LABELS[color].name} rolled ${value}` });
        setTimeout(() => executeMove(color, pickedIdx, value), 400);
    };

    // === FINALIZE ===
    const finalize = async (playerWon) => {
        setDone(playerWon ? 'win' : 'lose');
        const finalScore = playerWon ? score + 150 : score;
        if (playerWon) setScore(finalScore);
        const awarded = Math.max(40, Math.min(500, Math.round(finalScore * (playerWon ? 1.0 : 0.4))));
        try {
            await api.post(`/games/${game.code}/submit`, {
                score: Math.min(finalScore, 100),
                duration_seconds: 0,
                awarded_points: awarded,
                details: { won: playerWon, topic, score: finalScore, captures, learned: learnedCount, num_bots: numBots },
            });
            try {
                const { data: me } = await api.get('/auth/me');
                if (me?.badges?.some((b) => b.code === 'ludo_master')) setBadgeUnlocked(true);
            } catch { /* ignore */ }
        } catch (e) {
            console.warn('Game submission failed', e);
        }
        setTimeout(() => setWinOpen(true), 700);
        onPlayed && onPlayed();
    };

    // === RESET ===
    const reset = () => {
        const empty = EMPTY_TOKENS();
        tokensRef.current = empty; setTokens(empty);
        turnRef.current = 'yellow'; setTurn('yellow');
        doneRef.current = null;
        setDice(null); setRolling(false);
        setHighlighted(null); setLastMoved(null);
        setScore(0); setCaptures(0); setLearnedCount(0);
        setLog([{ kind: 'system', text: '🎲 New game — set up your match!', t: Date.now() }]);
        setDone(null); setBadgeUnlocked(false); setWinOpen(false);
        setQuestionModal(null); setStarModal(null); setTipModal(null); setBossModal(null);
        setTopicPickerOpen(true);
    };

    const closeWinAndExit = () => { setWinOpen(false); onPlayed && onPlayed(); };

    // === RENDER ===
    return (
        <div ref={wrapperRef} className={`relative overflow-x-hidden ${isFs ? 'bg-slate-950 h-screen w-screen p-2 sm:p-4 overflow-hidden flex items-center justify-center' : ''}`} data-testid="ludo-learning-game">
            <TopicPicker
                open={topicPickerOpen} presets={TOPIC_PRESETS}
                onPick={generateQuestions} onClose={skipToFallback}
                loading={aiLoading} errorText={aiError}
                numBots={numBots} onChangeNumBots={setNumBots}
            />

            <div className={isFs ? 'h-full w-full flex flex-col lg:flex-row gap-3 max-w-[1600px]' : 'grid lg:grid-cols-12 gap-4 lg:gap-5'}>
                <div className={isFs ? 'flex-1 min-h-0 flex' : 'lg:col-span-8'}>
                    {/* Ambient bokeh canvas — premium "casino felt" feel behind the board */}
                    <div className={`relative bg-gradient-to-br from-slate-900 via-slate-950 to-black rounded-3xl shadow-2xl ring-1 ring-white/10 overflow-hidden flex-1 flex flex-col ${isFs ? 'p-2 sm:p-3' : 'p-3 sm:p-5'}`}>
                        <AmbientBokeh />
                        <div className={`relative z-10 flex flex-col ${isFs ? 'h-full min-h-0' : ''}`}>
                            <BoardHeader turn={turn} topic={topic} muted={muted} onToggleMute={() => setMuted((m) => !m)} done={done} numBots={numBots} isFs={isFs} onToggleFs={toggleFs} />
                        {/* Live action prompt — always-visible banner so mobile users never miss the next step */}
                        <ActionPrompt
                            turn={turn} dice={dice} rolling={rolling}
                            highlighted={highlighted} latestLog={log[0]}
                            done={done} numBots={numBots} topicPickerOpen={topicPickerOpen}
                            anyModalOpen={!!questionModal || !!starModal || !!tipModal || !!bossModal}
                        />
                        <div className={`relative ${isFs ? 'flex-1 min-h-0 flex items-center justify-center mt-2' : 'mt-3'}`}>
                            <div className={isFs ? 'max-h-full max-w-full aspect-square h-full' : 'w-full'} style={isFs ? { aspectRatio: '1 / 1' } : undefined}>
                                <LudoBoard tokens={tokens} highlightedTokens={highlighted} onTokenClick={onPlayerTokenClick} lastMovedToken={lastMoved} activeColors={activeColors} />
                            </div>
                            {/* Per-player dice — positioned at 4 corners of the board frame, classic Ludo King style */}
                            <PerPlayerDiceCorners
                                activeColors={activeColors}
                                turn={turn}
                                dice={dice}
                                rolling={rolling}
                                callout={diceCallout}
                                onRoll={rollDice}
                                disabled={turn !== 'yellow' || done || topicPickerOpen || !!questionModal || !!starModal || !!tipModal || !!bossModal || !!highlighted}
                            />
                        </div>
                        {/* Confetti bursts on captures / home arrivals — render layered absolutely over the board */}
                        {bursts.map((b) => (
                            <ConfettiBurst key={b.id} count={b.count} scale={b.scale} />
                        ))}
                        {/* Celebratory announcement banner — "SIX! ROLL AGAIN", "CAPTURE!", "TOKEN HOME!" */}
                        <AnnouncementBanner announce={announce} />
                        {!isFs && (
                            <>
                                <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                                    <Scorecard
                                        score={score} captures={captures} learned={learnedCount}
                                        tokensHome={tokens.yellow.filter((p) => p === 57).length}
                                    />
                                </div>
                                <Legend />
                            </>
                        )}
                        </div>
                    </div>
                </div>

                {!isFs && (
                    <aside className="lg:col-span-4 flex flex-col gap-3">
                        <MatchLog entries={log} onReset={done ? reset : null} />
                    </aside>
                )}
            </div>

            <QuestionModal open={!!questionModal} kind={questionModal?.kind} question={questionModal?.question}
                onAnswer={questionModal?.onAnswer || (() => { })} onClose={() => setQuestionModal(null)} />
            <StarModal open={!!starModal} item={starModal} onClose={closeStar} />
            <TipModal open={!!tipModal} tip={tipModal} onClose={closeTip} />
            <BossModal open={!!bossModal} questions={bossModal?.questions || []} onComplete={completeBoss} />
            {/* Fireworks across the entire wrapper on victory */}
            {winOpen && done === 'win' && <Fireworks duration={4.5} density={1.6} />}
            <WinModal open={winOpen} won={done === 'win'} score={score} capturedCount={captures} learnedCount={learnedCount}
                badgeUnlocked={badgeUnlocked} onClose={closeWinAndExit} onPlayAgain={reset} />
        </div>
    );

    function randomQuestion() {
        const bank = questionBank.length ? questionBank : FALLBACK_QUESTIONS;
        return bank[Math.floor(Math.random() * bank.length)];
    }
}

// === SUB-COMPONENTS ===

// Animated celebration banner ("SIX! ROLL AGAIN", "CAPTURE!", "TOKEN HOME!")
// Swoops in from the top, scales with a slight overshoot, fades out.
function AnnouncementBanner({ announce }) {
    if (!announce) return null;
    const toneStyles = {
        gold:  { from: '#FFF200', to: '#E0A800', text: '#231F20', shadow: '0 0 30px rgba(255,242,0,0.8)' },
        red:   { from: '#E31E24', to: '#7F1010', text: '#FFFFFF', shadow: '0 0 30px rgba(227,30,36,0.8)' },
        green: { from: '#10B981', to: '#065F46', text: '#FFFFFF', shadow: '0 0 30px rgba(16,185,129,0.8)' },
    };
    const t = toneStyles[announce.tone] || toneStyles.gold;
    return (
        <AnimatePresence>
            <motion.div
                key={announce.id}
                initial={{ y: -80, scale: 0.5, opacity: 0, rotate: -8 }}
                animate={{ y: 0, scale: [0.5, 1.25, 1], opacity: 1, rotate: [-8, 4, 0] }}
                exit={{ y: -40, scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut', times: [0, 0.6, 1] }}
                className="absolute top-1/4 left-1/2 -translate-x-1/2 pointer-events-none z-40"
                data-testid="ludo-announce"
            >
                <div
                    className="px-6 py-3 rounded-2xl font-display font-extrabold text-2xl sm:text-3xl tracking-wide whitespace-nowrap"
                    style={{
                        background: `linear-gradient(180deg, ${t.from} 0%, ${t.to} 100%)`,
                        color: t.text,
                        boxShadow: t.shadow + ', 0 8px 24px rgba(0,0,0,0.6), inset 0 2px 0 rgba(255,255,255,0.55), inset 0 -3px 0 rgba(0,0,0,0.25)',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        border: '2px solid rgba(0,0,0,0.5)',
                    }}
                >
                    {announce.text}
                </div>
            </motion.div>
        </AnimatePresence>
    );
}

// Animated bokeh background — soft floating coloured orbs behind the board.
// Pure CSS / framer-motion (no canvas) so it's cheap on mobile.
function AmbientBokeh() {
    const orbs = [
        { color: '#FFF200', x: '10%', y: '20%', size: 220, delay: 0 },
        { color: '#E31E24', x: '78%', y: '18%', size: 180, delay: 1.5 },
        { color: '#10b981', x: '15%', y: '78%', size: 200, delay: 3 },
        { color: '#3b82f6', x: '82%', y: '76%', size: 220, delay: 4.5 },
        { color: '#a855f7', x: '50%', y: '50%', size: 260, delay: 6 },
    ];
    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
            {orbs.map((o, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0.25, scale: 0.8 }}
                    animate={{
                        opacity: [0.18, 0.32, 0.18],
                        scale: [0.85, 1.05, 0.85],
                        x: [0, 30, -20, 0],
                        y: [0, -20, 25, 0],
                    }}
                    transition={{ duration: 9 + i * 1.2, repeat: Infinity, ease: 'easeInOut', delay: o.delay }}
                    className="absolute rounded-full"
                    style={{
                        left: o.x, top: o.y,
                        width: o.size, height: o.size,
                        transform: 'translate(-50%, -50%)',
                        background: `radial-gradient(circle at center, ${o.color}66 0%, ${o.color}11 50%, transparent 75%)`,
                        filter: 'blur(28px)',
                    }}
                />
            ))}
            {/* Subtle scanline / felt texture */}
            <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                    backgroundImage:
                        'repeating-linear-gradient(45deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 6px)',
                }}
            />
        </div>
    );
}

// Live action prompt: always-visible banner telling user what's happening + what to do next.
// Critical on mobile where Match Log sits below the board (off-screen during play).
// Per-player dice positioned at the 4 OUTER CORNERS of the board, ALWAYS all 4 rendered.
function PerPlayerDiceCorners({ activeColors, turn, dice, rolling, callout, onRoll, disabled }) {
    const POSITIONS = {
        green:  { style: { top: '0.5%', left: '0.5%' },     pinRight: false },  // TL
        red:    { style: { top: '0.5%', right: '0.5%' },    pinRight: true  },  // TR
        yellow: { style: { bottom: '0.5%', left: '0.5%' },  pinRight: false },  // BL (you)
        blue:   { style: { bottom: '0.5%', right: '0.5%' }, pinRight: true  },  // BR
    };
    return (
        <>
            {['green', 'red', 'yellow', 'blue'].map((c) => {
                if (!activeColors.includes(c)) return null;
                const p = POSITIONS[c];
                const isActive = turn === c;
                const isInteractive = c === 'yellow' && !disabled;
                const myCallout = callout && callout.color === c ? callout : null;
                return (
                    <div key={c} className="absolute z-40 pointer-events-none" style={p.style} data-testid={`ludo-dice-${c}`}>
                        <div className="pointer-events-auto">
                            <DicePlate color={c} value={dice} active={isActive} rolling={isActive && rolling}
                                callout={myCallout}
                                onRoll={onRoll} interactive={isInteractive} pinRight={p.pinRight} />
                        </div>
                    </div>
                );
            })}
        </>
    );
}

const PLATE_THEMES = {
    yellow: { ring: '#C9A800', label: '#A07A00', baseDark: '#C9A800' },
    green:  { ring: '#065F46', label: '#065F46', baseDark: '#065F46' },
    red:    { ring: '#7F1010', label: '#7F1010', baseDark: '#7F1010' },
    blue:   { ring: '#1E3A8A', label: '#1E3A8A', baseDark: '#1E3A8A' },
};

const PIN_FILL = {
    yellow: '#FFD400', green: '#10B981', red: '#E31E24', blue: '#3B82F6',
};

// Dice plate: classic ivory dice with darker dots + a small color pin indicator next to it.
// pinRight: render pin on the RIGHT of the dice (used for top-right and bottom-right plates).
// callout: { value, isSix, key } — when set, floats a "🎲 N" or "🎲 6 ROLL AGAIN!" above the dice.
function DicePlate({ color, value, active, rolling, onRoll, interactive, pinRight = false, callout }) {
    return (
        <motion.div
            animate={active ? { y: [-2, 2, -2] } : {}}
            transition={active ? { repeat: Infinity, duration: 1.6 } : {}}
            className={`relative flex items-center gap-1.5 sm:gap-2 ${pinRight ? 'flex-row-reverse' : ''}`}
        >
            <ClassicDice value={value} active={active} rolling={rolling} onRoll={onRoll} interactive={interactive && color === 'yellow'} color={color} />
            <ColorPinIcon color={color} active={active} />
            <DiceCallout callout={callout} pinRight={pinRight} />
        </motion.div>
    );
}

// Floating "🎲 N" callout that pops above the dice when it lands.
// Style mirrors the celebratory banners — bright pill, drop shadow, bounce-in-then-fade animation.
function DiceCallout({ callout, pinRight }) {
    return (
        <AnimatePresence>
            {callout && (
                <motion.div
                    key={callout.key}
                    initial={{ opacity: 0, scale: 0.4, y: 10 }}
                    animate={{ opacity: 1, scale: [0.4, 1.25, 1], y: [10, -16, -14] }}
                    exit={{ opacity: 0, scale: 0.8, y: -20 }}
                    transition={{ duration: 0.55, ease: 'easeOut' }}
                    className={`absolute ${pinRight ? 'right-0' : 'left-0'} -top-9 pointer-events-none whitespace-nowrap`}
                    style={{ zIndex: 50 }}
                    data-testid="ludo-dice-callout"
                >
                    <div className={`px-2.5 py-1 rounded-xl text-[11px] sm:text-xs font-display font-extrabold tracking-wide ${callout.isSix
                        ? 'text-slate-900'
                        : 'text-white'
                        }`}
                        style={{
                            background: callout.isSix
                                ? 'linear-gradient(180deg, #FFF200 0%, #E0A800 100%)'
                                : 'linear-gradient(180deg, #1f2937 0%, #0f172a 100%)',
                            boxShadow: callout.isSix
                                ? '0 0 20px rgba(255,242,0,0.7), 0 4px 12px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -2px 0 rgba(0,0,0,0.2)'
                                : '0 4px 12px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -2px 0 rgba(0,0,0,0.3)',
                            border: '1.5px solid rgba(0,0,0,0.4)',
                            textShadow: callout.isSix ? '0 1px 0 rgba(255,255,255,0.3)' : '0 1px 1px rgba(0,0,0,0.5)',
                        }}>
                        {callout.isSix ? `🎲 ${callout.value} · ROLL AGAIN` : `🎲 ${callout.value}`}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function ColorPinIcon({ color, active }) {
    const fill = PIN_FILL[color];
    const dark = PLATE_THEMES[color].label;
    return (
        <div className="relative" style={{ width: 22, height: 28 }}>
            <svg viewBox="0 0 50 70" className="w-full h-full">
                <ellipse cx="25" cy="66" rx="9" ry="2.5" fill="rgba(0,0,0,0.35)" />
                <path d="M25 4 C13 4, 6 12, 6 24 C6 36, 18 50, 25 64 C32 50, 44 36, 44 24 C44 12, 37 4, 25 4 Z"
                    fill="#f1f5f9" stroke={dark} strokeWidth="2" />
                <circle cx="25" cy="22" r="11" fill={fill} stroke={dark} strokeWidth="1.5" />
                <ellipse cx="21" cy="18" rx="3.5" ry="2.2" fill="rgba(255,255,255,0.7)" />
                {active && (
                    <circle cx="25" cy="22" r="13" fill="none" stroke="#FFD700" strokeWidth="2">
                        <animate attributeName="stroke-width" values="2;4;2" dur="1s" repeatCount="indefinite" />
                    </circle>
                )}
            </svg>
        </div>
    );
}

function ClassicDice({ value, active, rolling, onRoll, interactive, color }) {
    const v = (active && value) ? value : 1;
    const dots = DICE_DOTS[v] || DICE_DOTS[1];
    const t = PLATE_THEMES[color];
    return (
        <div className="relative">
            <motion.button
                onClick={interactive ? onRoll : undefined}
                disabled={!interactive}
                animate={rolling
                    ? { rotateX: [0, 720, 1080], rotateY: [0, 720, 1080], scale: [1, 1.18, 1] }
                    : { rotateX: 0, rotateY: 0, scale: active ? 1 : 0.85 }}
                transition={{ duration: rolling ? 0.65 : 0.3 }}
                className={`w-11 h-11 sm:w-14 sm:h-14 rounded-xl relative flex items-center justify-center ${interactive ? 'cursor-pointer' : 'cursor-default'}`}
                style={{
                    background: 'linear-gradient(135deg, #FFFEF7 0%, #F5F0DC 60%, #D9CDA0 100%)',
                    border: `2.5px solid ${t.ring}`,
                    boxShadow: active
                        ? `0 0 0 4px ${t.ring}33, 0 6px 14px rgba(0,0,0,0.45), inset 0 2px 1px rgba(255,255,255,0.85), inset 0 -3px 2px rgba(0,0,0,0.15)`
                        : '0 3px 6px rgba(0,0,0,0.45), inset 0 2px 1px rgba(255,255,255,0.85), inset 0 -2px 2px rgba(0,0,0,0.15)',
                    transformStyle: 'preserve-3d',
                    opacity: active ? 1 : 0.85,
                }}
                data-testid={`ludo-corner-dice-clickable-${color}`}
            >
                {dots.map(([x, y], i) => (
                    <span key={i} className="absolute rounded-full" style={{
                        left: `${x}%`, top: `${y}%`,
                        width: '17%', height: '17%',
                        background: 'radial-gradient(circle at 35% 30%, #555 0%, #1a1a1a 70%, #000 100%)',
                        transform: 'translate(-50%, -50%)',
                        boxShadow: 'inset 0 -1px 1px rgba(0,0,0,0.5), 0 0.5px 0.5px rgba(255,255,255,0.4)',
                    }} />
                ))}
            </motion.button>
            {interactive && active && !rolling && (
                <motion.div
                    animate={{ y: [-4, 0, -4] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                    className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-base pointer-events-none">
                    👆
                </motion.div>
            )}
        </div>
    );
}

const DICE_DOTS = {
    1: [[50,50]],
    2: [[28,28],[72,72]],
    3: [[28,28],[50,50],[72,72]],
    4: [[28,28],[72,28],[28,72],[72,72]],
    5: [[28,28],[72,28],[50,50],[28,72],[72,72]],
    6: [[28,28],[72,28],[28,50],[72,50],[28,72],[72,72]],
};


function ActionPrompt({ turn, dice, rolling, highlighted, latestLog, done, numBots, topicPickerOpen, anyModalOpen }) {    if (topicPickerOpen || anyModalOpen || done) return null;
    let prompt = null; let tone = 'info';
    if (rolling) {
        prompt = turn === 'yellow' ? '🎲 Rolling…' : `${COLOR_LABELS[turn]?.emoji || '🤖'} ${COLOR_LABELS[turn]?.name || 'Bot'} is rolling…`;
        tone = 'info';
    } else if (highlighted && highlighted.color === 'yellow') {
        prompt = `👆 You rolled ${dice}. Tap one of your highlighted tokens to move it.`;
        tone = 'action';
    } else if (turn === 'yellow') {
        prompt = '🎲 Your turn — tap "Roll Dice" to play.';
        tone = 'action';
    } else {
        const label = COLOR_LABELS[turn]?.name || 'Bot';
        const emoji = COLOR_LABELS[turn]?.emoji || '🤖';
        prompt = `${emoji} ${label}'s turn — thinking…`;
        tone = 'info';
    }
    const lastText = latestLog?.text;
    const toneCls = tone === 'action'
        ? 'bg-gradient-to-r from-brand-yellow to-amber-400 text-slate-900 ring-amber-300'
        : 'bg-white/10 text-white ring-white/10';
    return (
        <motion.div
            key={prompt}
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className={`mt-3 px-3 py-2 sm:px-4 sm:py-2.5 rounded-2xl ring-1 backdrop-blur-sm flex items-center gap-2 ${toneCls}`}
            data-testid="ludo-action-prompt"
        >
            <div className="text-xs sm:text-sm font-display font-extrabold truncate flex-1">{prompt}</div>
            {lastText && (
                <div className="hidden sm:block text-[11px] text-slate-200 truncate max-w-[14rem] opacity-80" data-testid="ludo-action-recent">
                    Last: {lastText}
                </div>
            )}
        </motion.div>
    );
}

function BoardHeader({ turn, topic, muted, onToggleMute, done, numBots, isFs, onToggleFs }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-brand-yellow to-amber-400 flex items-center justify-center"><Crown className="h-5 w-5 sm:h-6 sm:w-6 text-slate-900" /></div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">Propulse Cables & Wires</div>
                <div className="font-display font-extrabold text-lg sm:text-2xl text-white truncate">Ludo Learning Arena</div>
                <div className="text-[11px] sm:text-xs text-slate-400 truncate">
                    {topic ? <>📚 {topic}</> : '⏳ Pick your match'} <span className="ml-1.5 text-slate-500">· vs {numBots} bot{numBots > 1 ? 's' : ''}</span>
                </div>
            </div>
            <div className="text-right hidden sm:block">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Turn</div>
                <motion.div animate={{ scale: done ? 1 : [1, 1.07, 1] }} transition={{ repeat: done ? 0 : Infinity, duration: 1.6 }}
                    className={`font-display font-extrabold text-base ${turn === 'yellow' ? 'text-brand-yellow' : turn === 'green' ? 'text-emerald-400' : turn === 'red' ? 'text-brand-red' : 'text-sky-400'}`}
                    data-testid="ludo-turn">
                    {COLOR_LABELS[turn].emoji} {COLOR_LABELS[turn].name.toUpperCase()}
                </motion.div>
            </div>
            <button onClick={onToggleFs} className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="ludo-fs-btn" title={isFs ? 'Exit full screen' : 'Full screen'}>
                {isFs ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button onClick={onToggleMute} className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="ludo-mute-btn">
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
        </div>
    );
}

function Scorecard({ score, captures, learned, tokensHome }) {
    return (
        <div className="flex-1 grid grid-cols-4 gap-2 text-center">
            <ScoreCell label="Pts" value={score} accent="from-amber-400 to-yellow-500" />
            <ScoreCell label="Home" value={`${tokensHome}/4`} accent="from-emerald-400 to-emerald-600" />
            <ScoreCell label="Caps" value={captures} accent="from-rose-400 to-rose-600" />
            <ScoreCell label="Learn" value={learned} accent="from-sky-400 to-blue-600" />
        </div>
    );
}
function ScoreCell({ label, value, accent }) {
    return (
        <div className={`rounded-2xl bg-gradient-to-br ${accent} p-2 sm:p-2.5 text-slate-900 shadow`}>
            <div className="text-[9px] uppercase tracking-wide font-bold">{label}</div>
            <div className="font-display font-extrabold text-lg sm:text-xl leading-none">{value}</div>
        </div>
    );
}

function Legend() {
    const items = [
        { Icon: HelpCircle, label: 'Question', color: 'text-sky-400' },
        { Icon: Star, label: 'Star (+10)', color: 'text-amber-300' },
        { Icon: Zap, label: 'Hazard', color: 'text-rose-400' },
        { Icon: Crown, label: 'Boss', color: 'text-violet-300' },
        { Icon: Lightbulb, label: 'Tip (+5)', color: 'text-yellow-300' },
        { Icon: ArrowBigRight, label: 'Shortcut', color: 'text-fuchsia-300' },
        { Icon: Shield, label: 'Safe', color: 'text-emerald-300' },
    ];
    return (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-7 gap-2 text-[10px] sm:text-[11px] text-slate-300">
            {items.map(({ Icon, label, color }) => (
                <div key={label} className="flex items-center gap-1.5 bg-white/5 rounded-xl px-2 py-1.5 border border-white/10">
                    <Icon className={`h-3.5 w-3.5 ${color}`} /> <span>{label}</span>
                </div>
            ))}
        </div>
    );
}

const PLAYER_GRADIENTS = {
    yellow: 'from-brand-yellow to-amber-400 text-slate-900',
    green:  'from-emerald-500 to-emerald-700 text-white',
    red:    'from-brand-red to-rose-700 text-white',
    blue:   'from-sky-500 to-blue-700 text-white',
};
const PLAYER_AURA = {
    yellow: '0 0 24px 6px rgba(255,242,0,0.55)',
    green:  '0 0 24px 6px rgba(16,185,129,0.55)',
    red:    '0 0 24px 6px rgba(227,30,36,0.55)',
    blue:   '0 0 24px 6px rgba(59,130,246,0.55)',
};

function PlayerCard({ color, tokens, active, isPlayer }) {
    const home = tokens.filter((p) => p === 57).length;
    const onBoard = tokens.filter((p) => p > 0 && p < 57).length;
    const inBase = tokens.filter((p) => p === 0).length;
    return (
        <motion.div
            animate={active ? { boxShadow: [PLAYER_AURA[color].replace('0.55', '0.4'), PLAYER_AURA[color], PLAYER_AURA[color].replace('0.55', '0.4')], scale: [1, 1.015, 1] } : { boxShadow: '0 4px 12px rgba(0,0,0,0.2)', scale: 1 }}
            transition={active ? { repeat: Infinity, duration: 1.6, ease: 'easeInOut' } : { duration: 0.3 }}
            className={`relative rounded-3xl p-3 sm:p-4 bg-gradient-to-br ${PLAYER_GRADIENTS[color]} ${active ? 'ring-4 ring-white/50' : 'opacity-95'}`}
            data-testid={`ludo-player-${color}`}
            style={{ boxShadow: active ? PLAYER_AURA[color] : '0 4px 12px rgba(0,0,0,0.2)' }}
        >
            <div className="flex items-center gap-2">
                {isPlayer ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                <div className="font-display font-extrabold text-base sm:text-lg">{COLOR_LABELS[color].name}</div>
                {active && (
                    <motion.span
                        animate={{ opacity: [0.6, 1, 0.6] }}
                        transition={{ repeat: Infinity, duration: 1.2 }}
                        className="ml-auto text-[10px] font-bold uppercase tracking-wide bg-black/25 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" /> Turn
                    </motion.span>
                )}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                <div><div className="text-[9px] uppercase font-bold opacity-80">Home</div><div className="font-display font-extrabold text-lg sm:text-xl">{home}/4</div></div>
                <div><div className="text-[9px] uppercase font-bold opacity-80">Track</div><div className="font-display font-extrabold text-lg sm:text-xl">{onBoard}</div></div>
                <div><div className="text-[9px] uppercase font-bold opacity-80">Base</div><div className="font-display font-extrabold text-lg sm:text-xl">{inBase}</div></div>
            </div>
        </motion.div>
    );
}

function MatchLog({ entries, onReset }) {
    return (
        <div className="bg-white rounded-3xl border border-slate-200 p-4 flex-1 min-h-[12rem]">
            <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-bold text-slate-900 inline-flex items-center gap-2 text-sm"><MessageSquareText className="h-4 w-4 text-slate-500" />Match Log</h3>
                {onReset && (
                    <button onClick={onReset} className="text-xs inline-flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid="ludo-reset-btn">
                        <RefreshCw className="h-3 w-3" /> Replay
                    </button>
                )}
            </div>
            <div className="space-y-1.5 max-h-[16rem] overflow-y-auto pr-1" data-testid="ludo-log">
                <AnimatePresence initial={false}>
                    {entries.map((e) => (
                        <motion.div key={e.t} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                            className={`text-[11px] sm:text-xs p-2 rounded-xl ${e.kind === 'player' ? 'bg-brand-yellow/20 text-slate-900' : e.kind === 'bot' ? 'bg-slate-50 text-slate-800' : 'bg-sky-50 text-sky-900'}`}>
                            {e.text}
                        </motion.div>
                    ))}
                    {entries.length === 0 && <div className="text-[11px] text-slate-400 italic">Roll to begin…</div>}
                </AnimatePresence>
            </div>
        </div>
    );
}

function sample(arr, n) {
    const out = []; const a = arr.slice();
    while (out.length < n && a.length) {
        const i = Math.floor(Math.random() * a.length);
        out.push(a.splice(i, 1)[0]);
    }
    return out;
}
